if (!customElements.get('product-last-variant')) {
  class ProductLastVariant extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      const variants = this.getVariantData();
      if (!variants?.length) return;

      this.currentVariant = this.getLastAvailableVariant(variants);
      if (!this.currentVariant) return;

      const run = () => {
        this.stripVariantFromUrl();
        this.applyVariant();
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
      } else {
        run();
      }
    }

    getVariantData() {
      const dataScript = this.querySelector('[type="application/json"]');
      if (!dataScript) return [];
      return JSON.parse(dataScript.textContent);
    }

    getLastAvailableVariant(variants) {
      for (let index = variants.length - 1; index >= 0; index -= 1) {
        if (variants[index].available) return variants[index];
      }
      return variants[variants.length - 1];
    }

    stripVariantFromUrl() {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('variant')) return;
      url.searchParams.delete('variant');
      window.history.replaceState({}, '', url.toString());
    }

    applyVariant() {
      this.removeErrorMessage();
      this.updateVariantInput();
      this.updateMedia();
      this.updatePickupAvailability();

      this.renderProductInfo()
        .then(() => {
          if (!this.currentVariant.available) this.setUnavailable();
        })
        .catch((error) => {
          console.error('product-last-variant:', error);
          this.updatePricesFromVariant();
        });
    }

    updateVariantInput() {
      const variantId = String(this.currentVariant.id);
      const associatedInputs = this.querySelectorAll('[data-product-last-variant-input]');

      associatedInputs.forEach((input) => {
        input.disabled = false;
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      const productForms = document.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
      );

      productForms.forEach((productForm) => {
        productForm.querySelectorAll('input[name="id"]').forEach((input) => {
          if (input.hasAttribute('data-product-last-variant-input')) return;
          input.value = variantId;
          input.disabled = true;
        });
      });

      document.dispatchEvent(
        new CustomEvent('product-last-variant:change', {
          bubbles: true,
          detail: { variant: this.currentVariant, sectionId: this.dataset.section },
        })
      );
    }

    updateMedia() {
      try {
        if (!this.currentVariant?.featured_media) return;

        const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
        if (mediaGallery?.setActiveMedia) {
          mediaGallery.setActiveMedia(
            `${this.dataset.section}-${this.currentVariant.featured_media.id}`,
            true
          );
        }

        const modalContent = document.querySelector(
          `#ProductModal-${this.dataset.section} .product-media-modal__content`
        );
        if (!modalContent) return;

        const newMediaModal = modalContent.querySelector(
          `[data-media-id="${this.currentVariant.featured_media.id}"]`
        );
        if (newMediaModal) modalContent.prepend(newMediaModal);
      } catch (error) {
        console.warn('product-last-variant: media update skipped', error);
      }
    }

    updatePickupAvailability() {
      try {
        const pickUpAvailability = document.querySelector('pickup-availability');
        if (!pickUpAvailability) return;

        if (
          this.currentVariant.available &&
          typeof pickUpAvailability.fetchAvailability === 'function'
        ) {
          pickUpAvailability.fetchAvailability(this.currentVariant.id);
          return;
        }

        pickUpAvailability.removeAttribute('available');
        pickUpAvailability.innerHTML = '';
      } catch (error) {
        console.warn('product-last-variant: pickup availability skipped', error);
      }
    }

    removeErrorMessage() {
      const section = document.getElementById(`MainProduct-${this.dataset.section}`);
      const productForm = section?.querySelector('product-form');
      if (productForm?.handleErrorMessage) productForm.handleErrorMessage();
    }

    renderProductInfo() {
      const sectionId = this.dataset.section;
      const productPath = new URL(this.dataset.url, window.location.origin).pathname;
      const sectionUrl = `${productPath}?variant=${this.currentVariant.id}&section_id=${encodeURIComponent(sectionId)}`;

      return fetch(sectionUrl)
        .then((response) => {
          if (!response.ok) throw new Error(`Section fetch failed (${response.status})`);
          return response.text();
        })
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const updated = this.updatePricesFromSection(html, sectionId);

          if (!updated) this.updatePricesFromVariant();

          this.toggleAddButton(
            !this.currentVariant.available,
            window.variantStrings?.soldOut
          );
        });
    }

    updatePricesFromSection(html, sectionId) {
      let updated = false;
      const priceDestination = document.getElementById(`price-${sectionId}`);
      const priceSource = html.getElementById(`price-${sectionId}`);

      if (priceSource && priceDestination) {
        priceDestination.innerHTML = priceSource.innerHTML;
        priceDestination.classList.remove('visibility-hidden');
        updated = true;
      }

      const productInfo = document.getElementById(`ProductInfo-${sectionId}`);
      const sourceProductInfo = html.getElementById(`ProductInfo-${sectionId}`);

      if (productInfo && sourceProductInfo) {
        const destinationPrices = productInfo.querySelectorAll('.price');
        const sourcePrices = sourceProductInfo.querySelectorAll('.price');

        destinationPrices.forEach((destinationPrice, index) => {
          const sourcePrice = sourcePrices[index];
          if (!sourcePrice) return;
          destinationPrice.innerHTML = sourcePrice.innerHTML;
          updated = true;
        });
      }

      return updated;
    }

    updatePricesFromVariant() {
      const productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      if (!productInfo || !this.currentVariant) return;

      const formattedPrice = this.formatMoney(this.currentVariant.price);
      const priceRoots = productInfo.querySelectorAll('.price');

      priceRoots.forEach((priceRoot) => {
        const regularPrice = priceRoot.querySelector('.price__regular .price-item--regular');
        const salePrice = priceRoot.querySelector('.price__sale .price-item--sale');

        if (regularPrice && !priceRoot.classList.contains('price--on-sale')) {
          this.setPriceText(regularPrice, formattedPrice);
        }

        if (salePrice) {
          this.setPriceText(salePrice, formattedPrice);
        }
      });

      productInfo.querySelectorAll('[data-product-last-variant-price]').forEach((node) => {
        this.setPriceText(node, formattedPrice);
      });
    }

    setPriceText(element, formattedPrice) {
      const leaves = element.matches('.price-item--regular')
        ? [element, ...element.querySelectorAll('.price-item--regular')]
        : element.querySelectorAll('.price-item--regular');

      const leaf = Array.from(leaves).find((node) => !node.querySelector('.price-item--regular')) || element;
      const text = leaf.textContent.trim();

      if (!text || !/[\d]/.test(text)) return;

      const prefix = text.match(/^[^\d]*/)?.[0]?.trim() || '';
      leaf.textContent = prefix ? `${prefix} ${formattedPrice}` : formattedPrice;
    }

    formatMoney(cents) {
      if (typeof window.Shopify?.formatMoney === 'function') {
        return window.Shopify.formatMoney(
          cents,
          this.dataset.moneyFormat || window.theme?.moneyFormat
        );
      }

      const format = this.dataset.moneyFormat || 'CHF {{amount}}';
      const amount = (cents / 100).toFixed(2);

      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toString())
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }

    toggleAddButton(disable = true, text) {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) return;

      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span');
      if (!addButton || !addButtonText) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.textContent = window.variantStrings?.addToCart || addButtonText.textContent;
      }
    }

    setUnavailable() {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) return;

      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span');
      const price = document.getElementById(`price-${this.dataset.section}`);

      if (!addButton || !addButtonText) return;

      addButtonText.textContent = window.variantStrings?.unavailable || 'Unavailable';
      addButton.setAttribute('disabled', 'disabled');
      if (price) price.classList.add('visibility-hidden');
    }
  }

  customElements.define('product-last-variant', ProductLastVariant);
}
